<template>
  <section class="section-base form-section-base">
    <div class="form-section-base__title">
      Сведения о кредиторе (если кредиторов несколько, раздел заполняется в
      отношении каждого кредитора)
    </div>

    <v-container>
      <div class="pb-5">
        <Creditor
          v-for="(v, index) in templateCreditors"
          :key="index"
          @delete="deleteCreditor(index)"
          @parsed-mrpa-change="parsedMrpaChangeHandler($event, index)"
          :v="v"
          :can-delete="creditors.length > 1"
          class="creditor"
        />

        <v-btn
          depressed
          color="primary"
          class="button-base"
          @click="addCreditor"
        >
          <v-icon size="20" left>{{ mdiAccountMultiplePlus }}</v-icon>
          Добавить кредитора
        </v-btn>
      </div>

      <Footer
        @prev-btn-click="
          () =>
            leaveStep({
              stepThis: this,
              direction: StepDirection.PREV,
              step: StepName.S2,
            })
        "
        @next-btn-click="
          () =>
            leaveStep({
              stepThis: this,
              direction: StepDirection.NEXT,
              step: StepName.S2,
            })
        "
        :loading-next="loadingNext"
      />
    </v-container>
  </section>
</template>

<script>
import { mdiAccountMultiplePlus } from '@mdi/js';
import cloneDeep from 'clone-deep';
import deepmerge from 'deepmerge';
import { Role } from '@/utils/dictionaries';
import Footer from '@/components/services/elements/Footer';
import { leaveStep } from '@/utils/services';
import { ServicesModule } from '@/store/modules/services';
import { StepDirection, StepName } from '@/types/services';
import Creditor, { creditorModel, getCreditorValidation } from './Creditor';
import { MrpaType, SubjectFillType } from '@/constants';
import {
  getMrpa,
  prefillMrpDisabled,
} from '@/components/services/elements/MrpaUploader';
import {
  assemblyManualTrustee,
  getDefaultManualTrustee,
} from '@/components/services/elements/TrusteeView';
import {
  assemblyMrpaTrustee,
  getDefaultMachineTrustee,
} from '@/components/services/elements/MrpaTrustee';

export default {
  name: 'Step2',
  props: {
    loadingNext: {
      type: Boolean,
      required: true,
    },
  },
  components: {
    Creditor,
    Footer,
  },
  data: () => ({
    mdiAccountMultiplePlus,
    StepDirection,
    StepName,
    leaveStep,
    invalidFields: [],
    creditors: [cloneDeep(creditorModel)],
  }),
  validations() {
    const validation = {
      creditors: {},
    };

    this.creditors.forEach((creditor, index) => {
      validation.creditors[index] = getCreditorValidation(creditor);
    });

    return validation;
  },
  computed: {
    templateCreditors() {
      const creditors = [];

      for (const key in this.$v.creditors) {
        if (
          Object.prototype.hasOwnProperty.call(this.$v.creditors, key) &&
          !key.startsWith('$')
        ) {
          creditors.push(this.$v.creditors[key]);
        }
      }

      return creditors;
    },
  },
  created() {
    this.prefillDraft();
  },
  methods: {
    prefillDraft() {
      if (!ServicesModule.statement.subjects.creditors?.length) return;

      this.creditors = ServicesModule.statement.subjects.creditors.map(
        (creditor, index) => this.getCreditor({ creditor, index })
      );
    },

    getCreditor({ creditor, index }) {
      const fillType = creditor.uiContext?.fillType ?? SubjectFillType.MANUAL;

      return {
        ...cloneDeep(creditorModel),
        number: index + 1,
        fillType,
        manual: this.getManualModel(creditor, fillType),
        machine: this.getMachineModel({ creditor, fillType }),
        account: deepmerge(creditorModel.account, creditor.account),
        details: deepmerge(creditorModel.details, creditor.details),
      };
    },

    getManualModel(creditor, fillType) {
      if (fillType === SubjectFillType.MACHINE) {
        return cloneDeep(creditorModel.manual);
      }

      if (creditor.person) {
        return deepmerge(creditorModel.manual, {
          role: Role.PERSON,
          person: creditor.person,
        });
      }

      if (creditor.organization) {
        return deepmerge(creditorModel.manual, {
          role: Role.ORGANIZATION,
          organization: {
            ...creditor.organization,
            trustee: getDefaultManualTrustee(creditor.trustee),
          },
        });
      }

      return deepmerge(creditorModel.manual, {
        role: Role.FOREIGN_ORGANIZATION,
        foreignOrganization: {
          ...creditor.foreignOrganization,
          trustee: getDefaultManualTrustee(creditor.trustee),
        },
      });
    },

    getMachineModel({ creditor, fillType, isMrpa }) {
      if (fillType === SubjectFillType.MANUAL) {
        return cloneDeep(creditorModel.machine);
      }

      if (isMrpa) {
        prefillMrpDisabled(creditor);
      }

      const baseFields = {
        formVisible: true,
        mrpa: getMrpa(creditor),
      };

      if (creditor.person) {
        return deepmerge(creditorModel.machine, {
          ...baseFields,
          role: Role.PERSON,

          person: {
            ...creditor.person,
            trustee: getDefaultMachineTrustee(creditor.trustee),
          },
        });
      }

      if (creditor.organization) {
        return deepmerge(creditorModel.machine, {
          ...baseFields,
          role: Role.ORGANIZATION,
          organization: {
            ...creditor.organization,
            trustee: getDefaultMachineTrustee(creditor.trustee),
          },
        });
      }

      return deepmerge(creditorModel.machine, {
        ...baseFields,
        role: Role.FOREIGN_ORGANIZATION,
        foreignOrganization: {
          ...creditor.foreignOrganization,
          trustee: getDefaultMachineTrustee(creditor.trustee),
        },
      });
    },

    parsedMrpaChangeHandler(mrpa, i) {
      this.creditors[i].machine = mrpa
        ? this.getMachineModel({
            creditor: mrpa,
            fillType: SubjectFillType.MACHINE,
            isMrpa: true,
          })
        : cloneDeep(creditorModel.machine);
    },

    addCreditor() {
      this.creditors.push(cloneDeep(creditorModel));
    },

    deleteCreditor(index) {
      this.creditors = this.creditors.filter((_, i) => i !== Number(index));
    },

    async touchFields() {
      this.invalidFields = [];

      for (const creditor of this.templateCreditors) {
        creditor.$touch();

        if (creditor.$invalid) {
          creditor.expand.$model = true;
          this.invalidFields.push(true);
          continue;
        }

        if (
          creditor.fillType === SubjectFillType.MACHINE &&
          creditor.machine.mrpa.type === MrpaType.BY_GUID
        ) {
          const isValid = await this.checkMrpaGuid(
            creditor.machine.mrpa.guid,
            creditor.machine.mrpa.issuerInn
          );

          if (!isValid) this.invalidFields.push(true);
        }
      }
    },

    assemblyModel() {
      return this.creditors.map((creditor) => {
        const cloneCreditor = cloneDeep(creditor);

        const baseModel = {
          details: cloneDeep(cloneCreditor.details),
          account: cloneDeep(cloneCreditor.account),
          uiContext: {
            fillType: cloneCreditor.fillType,
          },
        };

        if (cloneCreditor.fillType === SubjectFillType.MANUAL) {
          const { role } = cloneCreditor.manual;
          const { trustee } = cloneCreditor.manual[role];

          cloneCreditor.manual[role].trustee = null;

          return {
            ...baseModel,
            [role]: cloneDeep(cloneCreditor.manual[role]),
            trustee: trustee ? assemblyManualTrustee(trustee) : undefined,
          };
        }

        const { machine } = cloneCreditor;
        const { role } = machine;
        const { trustee } = machine[role];

        machine[role].trustee = null;

        return {
          ...baseModel,
          [role]: cloneDeep(machine[role]),
          trustee: assemblyMrpaTrustee({
            trustee,
            mrpa: machine.mrpa,
          }),
          mrpType: machine.mrpa.type,
        };
      });
    },
  },
};
</script>

<style scoped lang="scss">
.creditor {
  &:not(:first-child) {
    margin-bottom: 40px;
  }
}
</style>
